// Задания мини-тем A1 от движка (тикет 18 autopilot/issues): к заданиям,
// написанным по уроку (mini/a1.ts), - ещё по четырём видам на словах лексикона:
// выбрать, вписать, превратить, перевести (lib/grammar/drill-kinds.ts).
// Только то, что объяснено к уроку правила: формы и слова - те же, что в
// объяснении мини-темы; перевод - фраза целиком (lib/grammar/ru-phrase.ts).
import { affix, breakdownFrom, capitalize, sentenceRu, type BreakdownPart, type Exercise, type TransformExercise } from '../exercise'
import { derivedForm, nounForm, questionParticle, verbForm, type Person, type WordForm } from '../morph'
import { NOUNS, VERBS, type Noun, type Verb } from '../lexicon'
import { verbInfRu, verbPhraseRu } from '../ru-phrase'
import { fillEnding, translateEnding } from '../drill-kinds'
import { MARKERS, tenseExercise, type Marker, type TenseItem } from '../tense-markers'
import { RULES } from '../rules'

type Difficulty = 1 | 2 | 3

const pick = <T,>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)]
const half = () => Math.random() < 0.5
const PRON = ['Ben', 'Sen', 'O', 'Biz', 'Siz', 'Onlar'] as const
const dropPronoun = (s: string) => capitalize(s.split(' ').slice(1).join(' '))
const n = (tr: string) => NOUNS.find((x) => x.tr === tr)!

// Ступень → вид: 1 - выбрать, 2 - выбрать или вписать, 3 - превратить или перевести.
type Kind = 'choose' | 'fill' | 'transform' | 'translate'
const kindFor = (d: Difficulty): Kind => (d === 1 ? 'choose' : d === 2 ? (half() ? 'choose' : 'fill') : half() ? 'transform' : 'translate')

// ── Урок 14b: после числа - единственное (iki elma) ──────────────────────────
// Числа 2-4: в русском после них один падеж, родительный единственного (два яблока).
const NUMS: { tr: string; m: string; f: string }[] = [
  { tr: 'iki', m: 'два', f: 'две' },
  { tr: 'üç', m: 'три', f: 'три' },
  { tr: 'dört', m: 'четыре', f: 'четыре' },
]
// Счётные предметы: род (для «два / две»), родительный (два яблока) и множественное.
const COUNTED: { tr: string; ru: string; g: 'm' | 'f' | 'n'; gen: string; pl: string }[] = [
  { tr: 'elma', ru: 'яблоко', g: 'n', gen: 'яблока', pl: 'яблоки' },
  { tr: 'yumurta', ru: 'яйцо', g: 'n', gen: 'яйца', pl: 'яйца' },
  { tr: 'domates', ru: 'помидор', g: 'm', gen: 'помидора', pl: 'помидоры' },
  { tr: 'bilet', ru: 'билет', g: 'm', gen: 'билета', pl: 'билеты' },
  { tr: 'bardak', ru: 'стакан', g: 'm', gen: 'стакана', pl: 'стаканы' },
  { tr: 'valiz', ru: 'чемодан', g: 'm', gen: 'чемодана', pl: 'чемоданы' },
  { tr: 'kalem', ru: 'ручка', g: 'f', gen: 'ручки', pl: 'ручки' },
  { tr: 'defter', ru: 'тетрадь', g: 'f', gen: 'тетради', pl: 'тетради' },
  { tr: 'çanta', ru: 'сумка', g: 'f', gen: 'сумки', pl: 'сумки' },
  { tr: 'kitap', ru: 'книга', g: 'f', gen: 'книги', pl: 'книги' },
  { tr: 'harita', ru: 'карта', g: 'f', gen: 'карты', pl: 'карты' },
  { tr: 'şapka', ru: 'шляпа', g: 'f', gen: 'шляпы', pl: 'шляпы' },
]

function afterNumber(d: Difficulty): Exercise {
  const w = pick(COUNTED)
  const num = pick(NUMS)
  const numRu = w.g === 'f' ? num.f : num.m
  const many = nounForm({ tr: w.tr }, { plural: true }).word
  const why = 'после числа -ler не ставят: сколько штук, уже сказало число'
  const parts: BreakdownPart[] = [{ text: w.tr, label: 'единственное число', why }]
  const form: WordForm = { word: w.tr, morphs: [{ text: w.tr, label: 'единственное число', options: [], why }] }
  const answer = `${capitalize(num.tr)} ${w.tr}, lütfen.`
  const ru = `${capitalize(numRu)} ${w.gen}, пожалуйста.`
  switch (kindFor(d)) {
    case 'choose':
      return {
        kind: 'affix',
        topicId: '',
        base: w.tr,
        baseRu: w.ru,
        task: `${numRu} · после числа`,
        context: { tr: '___, lütfen.', ru },
        slots: [
          { answer: `${num.tr} `, options: [], label: '', why: '' },
          { answer: w.tr, options: half() ? [w.tr, many] : [many, w.tr], label: 'после числа', why },
        ],
        answer: `${num.tr} ${w.tr}`,
      }
    case 'fill':
      return { ...fillEnding('', { word: many, ru, answer, form, ask: `после числа ${num.tr}`, parts }), instruction: `Впишите слово: после числа ${num.tr}` }
    case 'translate':
      return translateEnding('', { ru, answer, form, parts })
    case 'transform':
      return {
        kind: 'transform',
        topicId: '',
        source: `${capitalize(many)} var.`,
        sourceRu: `Есть ${w.pl}.`,
        instruction: `Скажите, что их ${numRu}: ${num.tr} ...`,
        answer: `${capitalize(num.tr)} ${w.tr} var.`,
        variants: [],
        breakdown: parts,
      }
  }
}

// ── Урок 27: который час, saat üçte ──────────────────────────────────────────
const HOURS: { tr: string; at: string; is: string; half: string }[] = [
  { tr: 'bir', at: 'в час', is: 'час', half: 'второго' },
  { tr: 'iki', at: 'в два', is: 'два часа', half: 'третьего' },
  { tr: 'üç', at: 'в три', is: 'три часа', half: 'четвёртого' },
  { tr: 'dört', at: 'в четыре', is: 'четыре часа', half: 'пятого' },
  { tr: 'beş', at: 'в пять', is: 'пять часов', half: 'шестого' },
  { tr: 'altı', at: 'в шесть', is: 'шесть часов', half: 'седьмого' },
  { tr: 'yedi', at: 'в семь', is: 'семь часов', half: 'восьмого' },
  { tr: 'sekiz', at: 'в восемь', is: 'восемь часов', half: 'девятого' },
  { tr: 'dokuz', at: 'в девять', is: 'девять часов', half: 'десятого' },
  { tr: 'on', at: 'в десять', is: 'десять часов', half: 'одиннадцатого' },
  { tr: 'on bir', at: 'в одиннадцать', is: 'одиннадцать часов', half: 'двенадцатого' },
  { tr: 'on iki', at: 'в двенадцать', is: 'двенадцать часов', half: 'первого' },
]
const CLOCK_FRAMES: { tr: string; ru: (at: string) => string }[] = [
  { tr: 'Saat ___ görüşürüz.', ru: (at) => `Увидимся ${at}.` },
  { tr: 'Ders saat ___ başlıyor.', ru: (at) => `Урок начинается ${at}.` },
  { tr: 'Film saat ___.', ru: (at) => `Фильм ${at}.` },
  { tr: 'Saat ___ kalkıyorum.', ru: (at) => `Я встаю ${at}.` },
]

function clock(d: Difficulty): Exercise {
  const h = pick(HOURS)
  const isHalf = d > 1 && Math.random() < 0.3
  const time = isHalf ? `${h.tr} buçuk` : h.tr
  const at = isHalf ? `в половине ${h.half}` : h.at
  const frame = pick(CLOCK_FRAMES)
  const form = nounForm({ tr: time }, { case: 'loc' })
  form.morphs[form.morphs.length - 1] = { ...form.morphs[form.morphs.length - 1], label: 'в (время)' }
  const answer = frame.tr.replace('___', form.word)
  const ru = frame.ru(at)
  // В печатных видах с турецкой фразой основа - одно слово: «on bir» пропуск бы разрезал.
  const oneWord = !time.includes(' ')
  let kind = kindFor(d)
  if (!oneWord && (kind === 'fill' || kind === 'transform')) kind = 'translate'
  switch (kind) {
    case 'choose':
      return affix('', form, { tr: time, ru: at.replace(/^в /, '') }, at, { tr: frame.tr, ru })
    case 'fill':
      return fillEnding('', { word: time, ru, answer, form, ask: 'в (время)' })
    case 'translate':
      return translateEnding('', { ru, answer, form })
    case 'transform':
      return {
        kind: 'transform',
        topicId: '',
        source: `Saat ${time}.`,
        sourceRu: `${capitalize(h.is)}.`,
        instruction: `Скажите "${at}"`,
        answer: `Saat ${form.word}.`,
        variants: [`${capitalize(form.word)}.`],
        breakdown: breakdownFrom(form),
      }
  }
}

// ── Урок 47: время подсказывает слово во фразе (dün - -di, şimdi - -yor) ─────
// Пройдено к уроку: всё A1 - -yor и -di, с отрицанием; широкого и будущего нет.
const A1_PASSED = new Set(RULES.filter((r) => r.level === 'A1').map((r) => r.id))
const A1_CTX = { passed: A1_PASSED }
// Функцией, а не константой: tense-markers и этот файл импортируют друг друга через line.ts.
// şu an и şu anda в A1 не вводятся (карточка в A2), к уроку 47 их нет.
const A1_UNKNOWN_MARKERS = new Set(['şu an', 'şu anda'])
const a1Markers = () =>
  MARKERS.filter((m) => (m.t === 'present' || m.t === 'past') && !A1_UNKNOWN_MARKERS.has(m.tr))
const TENSE_VERBS = VERBS.filter((v) => v.complement && (!v.complement.rule || A1_PASSED.has(v.complement.rule)))
const MARKER_RU_AFTER_SUBJECT = new Set(['az önce'])

function markerFrame(m: Marker, v: Verb, p: Person, neg: boolean): { tr: string; ru: string } {
  const phrase = verbPhraseRu(v, m.t, p, { negative: neg }) ?? `${v.ru} ${v.complement!.ru}`
  const ru = MARKER_RU_AFTER_SUBJECT.has(m.tr)
    ? (() => {
        const [subj, ...rest] = phrase.split(' ')
        return `${capitalize(subj)} ${m.ru} ${rest.join(' ')}.`
      })()
    : `${capitalize(m.ru)} ${phrase}.`
  return { tr: `${PRON[p]} ${m.tr} ${v.complement!.tr} ___.`, ru }
}

function tenseReview(d: Difficulty): Exercise {
  const v = pick(TENSE_VERBS)
  const m = pick(a1Markers())
  const p = pick([0, 1, 2, 3, 4, 5] as Person[])
  const kind = kindFor(d)
  if (kind === 'choose') {
    const item: TenseItem = { marker: m, verb: v, p }
    return tenseExercise('', item, A1_CTX)
  }
  const neg = kind !== 'transform' && Math.random() < 0.3
  const form = verbForm(v, m.t, p, neg)
  const frame = markerFrame(m, v, p, neg)
  const answer = frame.tr.replace('___', form.word)
  const variants = [dropPronoun(answer)]
  if (kind === 'fill') return fillEnding('', { word: derivedForm(v, 'infinitive').word, ru: frame.ru, answer, form, ask: `${['я', 'ты', 'он', 'мы', 'вы', 'они'][p]}${neg ? ', не' : ''} · время подскажет фраза`, variants })
  if (kind === 'translate') return translateEnding('', { ru: frame.ru, answer, form, variants })
  // Превратить: та же фраза с маркером другого времени.
  const to = pick(a1Markers().filter((x) => x.t !== m.t))
  const toForm = verbForm(v, to.t, p)
  const toAnswer = `${PRON[p]} ${to.tr} ${v.complement!.tr} ${toForm.word}.`
  const ex: TransformExercise = {
    kind: 'transform',
    topicId: '',
    source: frame.tr.replace('___', verbForm(v, m.t, p).word),
    sourceRu: markerFrame(m, v, p, false).ru,
    instruction: `Скажите то же со словом ${to.tr} (${to.ru})`,
    answer: toAnswer,
    variants: [dropPronoun(toAnswer)],
    breakdown: breakdownFrom(toForm),
  }
  return ex
}

// Правило → генератор заданий его мини-темы.
export const MINI_MORE_A1: Record<string, (d: Difficulty) => Exercise> = {
  'noun-after-number-singular': afterNumber,
  'clock-time': clock,
  'tense-review-all': tenseReview,
}
